import { ColumnDef, Row } from '@tanstack/react-table'
import { tokens } from '@/locales/translations/tokens'
import {
  Clock,
  Calendar,
  ChevronRight,
  ChevronDown,
  Ticket,
  FileIcon,
  Upload,
  CircleX,
  MessageCircle,
  ExternalLink,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { UnreadMessagesBadge } from './unread-messages-badge'

const getStatusBadge = (status: string, t: (key: string) => string) => {
  switch (status) {
    case 'open':
      return (
        <Badge
          variant='secondary'
          className='min-w-[120px] justify-center truncate text-center'
        >
          {t(tokens.tickets.status.open)}
        </Badge>
      )
    case 'in_progress':
      return (
        <Badge
          variant='default'
          className='min-w-[120px] justify-center truncate text-center'
        >
          {t(tokens.tickets.status.in_progress)}
        </Badge>
      )
    case 'blocked':
      return (
        <Badge
          variant='destructive'
          className='min-w-[120px] justify-center truncate text-center'
        >
          {t(tokens.tickets.status.blocked)}
        </Badge>
      )
    case 'resolved':
      return (
        <Badge
          variant='default'
          className='min-w-[120px] justify-center truncate text-center'
        >
          {t(tokens.tickets.status.resolved)}
        </Badge>
      )
    default:
      return (
        <Badge
          variant='outline'
          className='min-w-[120px] justify-center truncate text-center'
        >
          {status}
        </Badge>
      )
  }
}

const getTypeBadge = (type: string, t: (key: string) => string) => {
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'technical_problems':
        return t(tokens.tickets.type.technical_problems)
      case 'integration_problems':
        return t(tokens.tickets.type.integration_problems)
      case 'security_issues':
        return t(tokens.tickets.type.security_issues)
      case 'suggestions':
        return t(tokens.tickets.type.suggestions)
      case 'others':
        return t(tokens.tickets.type.others)
      default:
        return type
    }
  }

  return <span className='text-sm font-semibold'>{getTypeLabel(type)}</span>
}

export function useTicketsTableColumns(
  onChatClick: (ticket: Ticket, e: React.MouseEvent) => void
): ColumnDef<Ticket>[] {
  const { t } = useTranslation()

  return [
    {
      accessorKey: 'title',
      header: t(tokens.tickets.table.title_column),
      cell: ({ row }) => {
        const ticket = row.original
        return (
          <div className='w-full'>
            <div className='mb-1 truncate font-medium'>{ticket.title}</div>
            <div className='truncate text-sm text-muted-foreground'>
              {ticket.description}
            </div>
          </div>
        )
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'type',
      header: t(tokens.tickets.table.type_column),
      cell: ({ row }) => {
        const type = row.getValue('type') as string
        return getTypeBadge(type, t)
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'created_at',
      header: t(tokens.tickets.table.created_date_column),
      cell: ({ row }) => {
        const date = row.getValue('created_at') as string
        return (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Calendar className='h-4 w-4' />
            {new Date(date).toLocaleDateString('pt-BR')}
          </div>
        )
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'deadline_resolution',
      header: t(tokens.tickets.table.deadline_column),
      cell: ({ row }) => {
        const deadline = row.getValue('deadline_resolution') as string
        return (
          <div className='text-sm text-muted-foreground'>
            {deadline ? (
              <div className='flex items-center gap-2'>
                <Clock className='h-4 w-4' />
                {new Date(deadline).toLocaleDateString('pt-BR')}
              </div>
            ) : (
              <span className='text-muted-foreground'>
                {t(tokens.tickets.table.deadline_undefined)}
              </span>
            )}
          </div>
        )
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: t(tokens.tickets.table.status_column),
      cell: ({ row }) => {
        const status = row.getValue('status') as string
        return getStatusBadge(status, t)
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const ticket = row.original
        const isResolved = ticket.status === 'resolved'

        return (
          <div className='flex justify-center'>
            <Button
              variant='ghost'
              size='sm'
              onClick={(e) => onChatClick(ticket, e)}
              className={`relative h-8 w-8 p-0 transition-colors`}
              title={
                isResolved
                  ? '✅ The ticket has been resolved'
                  : 'Chat with support'
              }
            >
              <MessageCircle className='h-8 w-8' />
              <UnreadMessagesBadge chat={ticket.chat} />
            </Button>
          </div>
        )
      },
      enableSorting: false,
      enableHiding: false,
    },
  ]
}

export function renderTableHeader(t: (key: string) => string) {
  return (
    <div className='sticky top-0 z-10 grid grid-cols-[48px_1fr_180px_140px_120px_120px_80px] gap-4 border-b bg-background'>
      <div className='p-3 text-center text-sm font-medium text-muted-foreground'></div>
      <div className='p-3 text-left text-sm font-medium'>
        {t(tokens.tickets.table.title_column)}
      </div>
      <div className='p-3 text-left text-sm font-medium'>
        {t(tokens.tickets.table.type_column)}
      </div>
      <div className='p-3 text-center text-sm font-medium'>
        {t(tokens.tickets.table.created_date_column)}
      </div>
      <div className='p-3 text-center text-sm font-medium'>
        {t(tokens.tickets.table.deadline_column)}
      </div>
      <div className='p-3 text-center text-sm font-medium'>
        {t(tokens.tickets.table.status_column)}
      </div>
      <div className='p-3 text-center text-sm font-medium'>
        {t(tokens.tickets.table.actions_column)}
      </div>
    </div>
  )
}

export function renderTableRow(
  row: Row<Ticket>,
  handleRowExpand: (row: Row<Ticket>) => void,
  handleChatClick: (ticket: Ticket, e: React.MouseEvent) => void,
  onCloseTicket: (ticketId: string) => void,
  onUploadEvidence: (ticketId: string, file: File) => void,
  isClosingTicket: boolean = false,
  isUploadingEvidence: boolean = false,
  t: (key: string) => string
) {
  const ticket = row.original
  const isResolved = ticket.status === 'resolved'

  return (
    <div key={row.id}>
      <div
        className='grid cursor-pointer grid-cols-[48px_1fr_180px_140px_120px_120px_80px] gap-4 border-b transition-colors hover:bg-muted/50'
        onClick={() => handleRowExpand(row)}
      >
        <div className='flex items-center justify-center p-3'>
          {row.getIsExpanded() ? (
            <ChevronDown className='h-4 w-4 text-muted-foreground' />
          ) : (
            <ChevronRight className='h-4 w-4 text-muted-foreground' />
          )}
        </div>
        {row.getVisibleCells().map((cell) => (
          <div key={cell.id} className='flex items-center p-3'>
            {cell.column.id === 'title' ? (
              <div className='w-full max-w-[350px]'>
                <div className='mb-1 truncate font-medium'>
                  {row.original.title}
                </div>
                <div className='truncate text-sm text-muted-foreground'>
                  {row.original.description}
                </div>
              </div>
            ) : cell.column.id === 'type' ? (
              getTypeBadge(row.original.type, t)
            ) : cell.column.id === 'created_at' ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Calendar className='h-4 w-4' />
                {new Date(row.original.created_at).toLocaleDateString('pt-BR')}
              </div>
            ) : cell.column.id === 'deadline_resolution' ? (
              <div className='text-sm text-muted-foreground'>
                {row.original.deadline_resolution ? (
                  <div className='flex items-center gap-2'>
                    <Clock className='h-4 w-4' />
                    {new Date(
                      row.original.deadline_resolution
                    ).toLocaleDateString('pt-BR')}
                  </div>
                ) : (
                  <span className='text-muted-foreground'>
                    {t(tokens.tickets.table.deadline_undefined)}
                  </span>
                )}
              </div>
            ) : cell.column.id === 'status' ? (
              getStatusBadge(row.original.status, t)
            ) : cell.column.id === 'actions' ? (
              <div className='flex justify-center'>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={(e) => handleChatClick(row.original, e)}
                  title={
                    isResolved
                      ? t(tokens.tickets.table.chat_resolved_tooltip)
                      : t(tokens.tickets.table.chat_available_tooltip)
                  }
                  className="relative"
                >
                  <MessageCircle className='h-8 w-8' />
                  <UnreadMessagesBadge chat={row.original.chat} />
                </Button>
              </div>
            ) : (
              <span>{String(cell.getValue())}</span>
            )}
          </div>
        ))}
      </div>

      {row.getIsExpanded() && (
        <div className='border-b bg-muted/50 p-6'>
          <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
            <div className='space-y-3'>
              <div>
                <label className='text-sm font-medium'>
                  {t(tokens.tickets.detail.title_label)}
                </label>
                <div className='mt-1 flex items-center gap-2 rounded-md border bg-background p-2 text-sm'>
                  <Ticket className='h-4 w-4 text-muted-foreground' />
                  {row.original.title}
                </div>
              </div>
              <div>
                <label className='text-sm font-medium'>
                  {t(tokens.tickets.detail.deadline_label)}
                </label>
                <div className='mt-1 flex items-center gap-2 rounded-md border bg-background p-2 text-sm'>
                  <Clock className='h-4 w-4 text-muted-foreground' />
                  {row.original.deadline_resolution
                    ? new Date(
                        row.original.deadline_resolution
                      ).toLocaleDateString('pt-BR')
                    : t(tokens.tickets.table.deadline_not_estimated)}
                </div>
              </div>
            </div>
            <div>
              <label className='text-sm font-medium'>
                {t(tokens.tickets.detail.description_label)}
              </label>
              <div className='mt-1 max-h-[200px] min-h-[120px] overflow-y-auto rounded-md border bg-background p-3 text-sm'>
                <div className='whitespace-pre-wrap break-words'>
                  {row.original.description}
                </div>
              </div>
            </div>
          </div>

          {row.original.attachments.length > 0 && (
            <div>
              <div className='flex items-center gap-2'>
                <label className='text-sm font-medium'>
                  {t(tokens.tickets.detail.attachments_label)}
                </label>
                <div className='flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground'>
                  {row.original.attachments.length}
                </div>
              </div>
              <div className='mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3'>
                {row.original.attachments.map(
                  (attachment: TicketAttachment) => (
                    <div
                      key={attachment.external_id}
                      onClick={(e) => {
                        e.stopPropagation()
                        window.open(attachment.evidence_url, '_blank')
                      }}
                      className='group relative flex min-w-0 cursor-pointer items-center gap-2 rounded-md border bg-background p-2 transition-colors hover:bg-muted/50'
                    >
                      <div className='flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary'>
                        <FileIcon className='h-3 w-3' />
                      </div>
                      <div className='min-w-0 flex-1'>
                        <div className='mb-1 flex items-center gap-2'>
                          <div className='truncate text-sm font-medium'>
                            {attachment.name}
                          </div>
                          <Badge
                            variant={
                              attachment.type === 'user'
                                ? 'secondary'
                                : 'default'
                            }
                            className='px-1 py-0 text-xs'
                          >
                            {attachment.type === 'user'
                              ? 'User'
                              : t(tokens.tickets.chat.support_label)}
                          </Badge>
                        </div>
                        <div className='truncate text-xs text-muted-foreground'>
                          {attachment.extension.toUpperCase()} •{' '}
                          {new Date(attachment.uploaded_at).toLocaleDateString(
                            'pt-BR'
                          )}
                        </div>
                      </div>
                      {attachment.evidence_url && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            window.open(attachment.evidence_url, '_blank')
                          }}
                          className='rounded p-1 opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100'
                          title='Abrir em nova guia'
                        >
                          <ExternalLink className='h-3 w-3 text-muted-foreground hover:text-primary' />
                        </button>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          <Separator className='my-6' />

          <div className='space-y-4'>
            <div className='flex justify-between gap-3'>
              <div className='relative'>
                <input
                  id={`evidence-upload-${row.original.external_id}`}
                  type='file'
                  className='hidden'
                  accept='.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg'
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      onUploadEvidence(row.original.external_id, file)
                      e.target.value = ''
                    }
                  }}
                />
                <Button
                  size='sm'
                  variant='outline'
                  disabled={isResolved || isUploadingEvidence}
                  className={
                    isResolved ? 'cursor-not-allowed text-muted-foreground' : ''
                  }
                  title={
                    isResolved
                      ? t(tokens.tickets.table.evidence_resolved_tooltip)
                      : t(tokens.tickets.table.evidence_button)
                  }
                  onClick={() => {
                    if (!isResolved && !isUploadingEvidence) {
                      document
                        .getElementById(
                          `evidence-upload-${row.original.external_id}`
                        )
                        ?.click()
                    }
                  }}
                >
                  {isUploadingEvidence ? (
                    <div className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
                  ) : (
                    <Upload className='h-4 w-4' />
                  )}
                  {isUploadingEvidence
                    ? 'Enviando...'
                    : t(tokens.tickets.table.evidence_button)}
                </Button>
              </div>
              <Button
                variant='destructive'
                size='sm'
                onClick={() => onCloseTicket(row.original.external_id)}
                disabled={isResolved || isClosingTicket}
                className={
                  isResolved ? 'cursor-not-allowed text-muted-foreground' : ''
                }
                title={
                  isResolved
                    ? t(tokens.tickets.table.close_resolved_tooltip)
                    : t(tokens.tickets.table.close_button)
                }
              >
                <CircleX className='h-4 w-4' />
                {t(tokens.tickets.table.close_button)}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function renderNoResults(t: (key: string) => string) {
  return (
    <div className='grid grid-cols-[48px_1fr_180px_140px_120px_120px_80px] gap-4'>
      <div className='col-span-7 p-6 text-center'>
        {t(tokens.tickets.table.no_results)}
      </div>
    </div>
  )
}
